import React, { useState, useEffect, useRef } from 'react';
import AmiLogo from '../assets/icons/amiwareLogo.png';
import "../assets/css/style.css";

const ChatMessage = ({ text, isUser }) => {
    const [displayedText, setDisplayedText] = useState('');
    const [isTyping, setIsTyping] = useState(!isUser);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!isUser) {
            setDisplayedText('');
            setIsTyping(true);
            let index = 0;

            const startDelay = setTimeout(() => {
                const typingInterval = setInterval(() => {
                    if (index < text.length) {
                        setDisplayedText(text.substring(0, index + 1));
                        index++;
                    } else {
                        setIsTyping(false);
                        clearInterval(typingInterval);
                    }
                }, 15);
            }, 100);

            return () => clearTimeout(startDelay);
        } else {
            setDisplayedText(text);
            setIsTyping(false);
        }
    }, [text, isUser]);

    const handleCopy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className={`d-flex ${isUser ? 'justify-content-end' : 'justify-content-start'} align-items-start mb-3`}>
            {!isUser && (
                <div className="mr-1">
                    <img
                        src={AmiLogo}
                        alt="Ami Logo"
                        className="rounded-circle"
                        style={{
                            width: '35px',
                            height: '35px',
                            border: '1px solid gold',
                            padding: '2px'
                        }}
                    />
                </div>
            )}
            <div
                className="p-3 position-relative text-white"
                style={{
                    backgroundColor: isUser ? '#28a745' : '#000',
                    borderRadius: '15px',
                    border: isUser ? '1px solid rgba(255,255,255,0.2)' : '1px solid gold',
                    maxWidth: '98%',
                }}
            >
                {!isUser && <div className="fw-bold fs-6 mb-1 text-warning">Ami</div>}
                <p className="m-0 fs-5">{displayedText}{isTyping && <span className="text-muted">▋</span>}</p>
                {!isUser && (
                    <button onClick={handleCopy} className="btn btn-sm btn-outline-light mt-2">
                        {copied ? '✓' : '📋'}
                    </button>
                )}
            </div>
        </div>
    );
};

const ChatInput = ({ input, isLoading, onInputChange, onSubmit }) => {
    return (
        <form onSubmit={onSubmit} className="p-1">
            <div className="input-group">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => onInputChange(e.target.value)}
                    placeholder="Type your message..."
                    disabled={isLoading}
                    className="form-control text-white bg-dark border border-secondary rounded-pill px-3"
                />
                <button type="submit" disabled={isLoading} className="btn btn-primary rounded-pill ms-2 ml-2">
                    {isLoading ? <span className="spinner-border spinner-border-sm"></span> : '➤'}
                </button>
            </div>
        </form>
    );
};

const AmiChat = () => {
    const [messages, setMessages] = useState([
        { text: "Hello, My name is Ami. I'm an AI assistant. How can I help you today?", isUser: false }
    ]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (input.trim() && !isLoading) {
            const userMessage = input.trim();
            const body = JSON.stringify({ user_prompt: userMessage });
            setInput("");
            setMessages((prev) => [...prev, { text: userMessage, isUser: true }]);
            setIsLoading(true);

            try {
                const response = await fetch("https://disha-pocket.neophyte.live/hrms-ami/chat", {
                    method: "POST",
                    headers: { 
                        "Content-Type": "application/json",
                        "Accept": "application/json"
                    },
                    body,
                    mode: "cors"
                });

                if (!response.ok) {
                    throw new Error("Failed to fetch response");
                }

                const result = await response.json();
                console.log("API result:", result);

                // Extracting the message and storing it
                const botMessage = result.final_output || "No response received.";
                setMessages((prev) => [...prev, { text: botMessage, isUser: false }]);
    
            } catch (error) {
                console.error("Error:", error);
                setMessages((prev) => [
                    ...prev,
                    { text: "I'm having trouble connecting. Please try again later.", isUser: false },
                ]);
            } finally {
                setIsLoading(false);
            }
        }
    };
    
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    return (
        <div className="container-fluid h-100 d-flex align-items-center justify-content-center pt-3" style={{ backgroundColor: '#0a192f' }}>
            <div
                className="card text-white w-100 h-100 d-flex flex-column"
                style={{
                    backgroundColor: '#0a192f',
                    maxHeight: "100%",
                }}
            >
                {/* Ensure chat messages don't overflow */}
                <div
                    className="card-body flex-grow-1"
                    ref={messagesContainerRef}
                    style={{
                        overflowY: 'auto',  
                        scrollbarWidth: 'none', 
                        msOverflowStyle: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: "100vh", // Prevent overflow
                    }}
                >
                    {messages.map((msg, index) => (
                        <ChatMessage key={index} text={msg.text} isUser={msg.isUser} />
                    ))}
                    <div ref={messagesEndRef} />
                </div>
                {/* Chat Input */}
                <div className="card-footer p-2" style={{ backgroundColor: '#0a192f' }}>
                    <ChatInput input={input} isLoading={isLoading} onInputChange={setInput} onSubmit={handleSubmit} />
                </div>
            </div>
        </div>
    );
};

export default AmiChat;
